<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { blownInsulation, drywall, screed } from '@/actions/App/Http/Controllers/ServiceController'

const posts = [
    {
        id: 1,
        title: 'Montáže sádrokartonu',
        href: drywall.url(),
        imageUrl: '/src/services/montaze-sadrokartonu-ukazka.webp',
    },
    {
        id: 2,
        title: 'Foukaná izolace',
        href: blownInsulation.url(),
        imageUrl: '/src/services/foukana-izolace-ukazka.webp',
    },
]
</script>

<template>
    <AppLayout
        meta_title="Betonové stěrky Olomouc - moderní betonový vzhled"
        meta_description="Betonové stěrky do interiéru - moderní povrchová úprava stěn a podlah. Vhodné pro byty, domy i komerční prostory."
        :meta_canonical="screed.url()"
    >
        <template #content>
            <section class="section-width mt-16">
                <header class="max-w-4xl">
                    <h1 class="header-h">Betonové stěrky Olomouc</h1>
                    <p class="mt-6 text-xl/8 text-balance text-gray-700">
                        Betonové stěrky do interiéru - moderní povrchová úprava stěn a podlah. Vhodné pro byty, domy i
                        komerční prostory.
                    </p>
                </header>

                <section
                    class="relative -top-32 mt-20 grid grid-cols-1 overflow-hidden pt-32 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-8"
                >
                    <div class="lg:pr-8">
                        <h2 class="text-2xl font-semibold tracking-tight text-pretty text-gray-900">
                            Jak probíhá provedení betonové stěrky
                        </h2>
                        <p class="mt-6 text-base/7 text-gray-600">
                            Betonová stěrka je moderní povrchová úprava, která se používá na podlahy, stěny, schodiště
                            nebo i kuchyňské linky. Oblíbená je pro svůj industriální vzhled, odolnost a snadnou údržbu.
                        </p>

                        <div class="mt-8 space-y-6">
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">1. Příprava podkladu</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Podklad musí být pevný, suchý a zbavený prachu či mastnot.</li>
                                    <li>Nerovnosti se vyrovnají, případně se nanese penetrace pro lepší přilnavost.</li>
                                    <li>U podlah se kontroluje rovinnost, u stěn pak soudržnost omítek.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">2. Nanesení základní vrstvy</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Na připravený povrch se nanese první vrstva stěrky.</li>
                                    <li>
                                        Obvykle se používá nerezové hladítko, kterým se materiál rovnoměrně roztahuje.
                                    </li>
                                    <li>Tato vrstva slouží k vytvoření pevného podkladu a sjednocení povrchu.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">3. Aplikace finální vrstvy</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Po zaschnutí se nanáší druhá (někdy i třetí) vrstva stěrky.</li>
                                    <li>
                                        Při práci se tvoří charakteristická textura a barevné stíny, které dělají
                                        betonovou stěrku jedinečnou.
                                    </li>
                                    <li>
                                        Povrch se může ještě vyhladit nebo ponechat hrubší, podle požadovaného vzhledu.
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">4. Broušení a úpravy</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>
                                        Po vytvrzení se povrch lehce přebrousí, aby byl hladký a bez ostrých nerovností.
                                    </li>
                                    <li>Odstraní se případné nedokonalosti.</li>
                                </ul>
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-900">5. Ochranný nátěr</h3>
                                <ul class="mt-2 list-disc space-y-1 pl-5 text-sm/6 text-gray-600">
                                    <li>Nakonec se betonová stěrka ošetří ochranným lakem, voskem nebo penetrací.</li>
                                    <li>Tento krok zvyšuje odolnost proti vodě, skvrnám a mechanickému opotřebení.</li>
                                    <li>V koupelnách a kuchyních je tento nátěr nezbytný.</li>
                                </ul>
                            </div>

                            <div class="border-brand-orange mt-8 border-l-2 bg-orange-50 p-4">
                                <p class="text-sm/6 text-orange-900">
                                    <span class="font-semibold">Betonová stěrka</span> je designový prvek, který v
                                    interiéru vytváří moderní, minimalistický a odolný povrch. Hodí se jak do
                                    novostaveb, tak do rekonstrukcí a lze ji provést téměř na jakýkoliv pevný podklad.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mt-4 flex flex-col justify-center gap-8 sm:justify-end sm:pl-20 md:flex-row lg:-mt-40 lg:pl-0"
                    >
                        <div
                            class="mx-auto w-72 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:w-50 lg:pt-36 xl:w-72 xl:pt-80"
                        >
                            <div class="relative">
                                <img
                                    alt="Betonová stěrka Olomouc a okolí"
                                    src="/src/services/betonova-sterka-ukazka.webp"
                                    class="aspect-square w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                />
                                <div
                                    class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-gray-900/10 ring-inset"
                                />
                            </div>
                        </div>
                        <div class="mx-auto w-72 flex-none space-y-8 sm:pt-52 lg:w-50 lg:pt-36 xl:w-72">
                            <div class="relative">
                                <img
                                    alt="Betonová stěrka v interiéru"
                                    src="/src/services/betonova-sterka_02.webp"
                                    class="aspect-square w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                />
                                <div
                                    class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-gray-900/10 ring-inset"
                                />
                            </div>
                            <div class="relative">
                                <img
                                    alt="Betonová stěrka Olomouc"
                                    src="/src/services/betonova-sterka_03.webp"
                                    class="aspect-square w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                />
                                <div
                                    class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-gray-900/10 ring-inset"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-10 lg:col-span-1 lg:mt-0">
                        <p class="text-base/7 font-semibold text-gray-500">Provádíme</p>
                        <hr class="my-2 border-t border-gray-200" />
                        <ul class="grid list-inside list-disc grid-cols-1 gap-y-4">
                            <li class="text-sm/6 text-gray-600">
                                kompletní výstavba podkroví včetně podlah z fermacellu
                            </li>
                            <li class="text-sm/6 text-gray-600">podhledy a příčky v rodinných domech</li>
                            <li class="text-sm/6 text-gray-600">různé desingové prvky</li>
                            <li class="text-sm/6 text-gray-600">rozdělení velké místnosti na více jednotek příčkou</li>
                            <li class="text-sm/6 text-gray-600">kazetové podhledy</li>
                        </ul>
                    </div>

                    <div class="mt-10 lg:col-span-1 lg:mt-0">
                        <p class="text-base/7 font-semibold text-gray-500">Nástroje a příslušentví</p>
                        <hr class="my-2 border-t border-gray-200" />
                        <ul class="grid list-inside list-disc grid-cols-1 gap-y-4">
                            <li class="text-sm/6 text-gray-600">sádrokartonové desky</li>
                            <li class="text-sm/6 text-gray-600">profilové plechy</li>
                            <li class="text-sm/6 text-gray-600">montážní materiál</li>
                            <li class="text-sm/6 text-gray-600">izolační materiály</li>
                            <li class="text-sm/6 text-gray-600">nástroje pro montáž</li>
                        </ul>
                    </div>

                    <div class="col-span-full mt-10">
                        <div class="mx-auto max-w-2xl text-center">
                            <h2 class="header-h text-center">Další služby</h2>
                        </div>
                        <div
                            class="bg-brand-orange mx-auto mt-5 grid max-w-2xl grid-cols-1 gap-5 rounded-4xl p-4 lg:mx-0 lg:max-w-none lg:grid-cols-2"
                        >
                            <article
                                v-for="post in posts"
                                :key="post.id"
                                class="relative flex flex-col items-start justify-between"
                            >
                                <div class="relative w-full">
                                    <img
                                        :src="post.imageUrl"
                                        alt=""
                                        class="aspect-video w-full rounded-2xl bg-gray-100 object-cover sm:aspect-2/1 lg:aspect-3/2"
                                    />
                                    <div class="absolute inset-0 rounded-2xl inset-ring inset-ring-gray-900/10" />
                                </div>

                                <div class="flex w-full justify-center">
                                    <h3
                                        class="font-chivo mt-3 text-xl font-semibold tracking-tight text-white sm:text-3xl"
                                    >
                                        {{ post.title }}
                                    </h3>
                                </div>

                                <Link
                                    :href="post.href"
                                    class="absolute inset-0"
                                />
                            </article>
                        </div>
                    </div>
                </section>
            </section>
        </template>
    </AppLayout>
</template>
